import {
  COINS_PER_UNIT_IN_BAG,
  DEFAULT_BOARD_ID,
  DEFAULT_CONFIG,
} from "./data/default-config";
import type { PlayerId } from "./types/common";
import type { WarchestDataConfig } from "./types/game-data-config";
import type {
  BoardCell,
  LocationState,
  PlayerZones,
  WarchestState,
} from "./types/game-state";
import { fnv1aHash, shuffleBag } from "./utils/rng";
// Register tactics as a side effect
import "./data/tactics";

export interface InitGameOptions {
  readonly p1UnitTypes?: readonly string[];
  readonly p2UnitTypes?: readonly string[];
  readonly boardId?: string;
  readonly config?: WarchestDataConfig;
  readonly seed?: number;
}

const DEFAULT_P1_UNITS = [
  "SWORDSMAN",
  "PIKEMAN",
  "CROSSBOWMAN",
  "LIGHT_CAVALRY",
] as const;
const DEFAULT_P2_UNITS = ["ARCHER", "CAVALRY", "LANCER", "SCOUT"] as const;

export function buildPlayerZones(
  unitTypes: readonly string[],
  rngState: number,
  config: WarchestDataConfig,
): { zones: PlayerZones; rngState: number } {
  const bag: string[] = [];
  const supply: string[] = [];

  for (const unitType of unitTypes) {
    const unitDef = config.units[unitType];
    if (!unitDef) throw new Error(`Unknown unit type: ${unitType}`);
    for (let i = 0; i < COINS_PER_UNIT_IN_BAG; i += 1) {
      bag.push(unitType);
    }
    const supplyCount = unitDef.coinCount - COINS_PER_UNIT_IN_BAG;
    for (let i = 0; i < supplyCount; i += 1) {
      supply.push(unitType);
    }
  }
  // Add Royal Coin to bag
  bag.push("ROYAL");

  const { shuffled, nextState } = shuffleBag(bag, rngState);

  return {
    zones: {
      bag: shuffled,
      hand: [],
      discardFaceDown: [],
      discardFaceUp: [],
      supply,
      removed: [],
    },
    rngState: nextState,
  };
}

function buildInitialLocations(boardDef: {
  readonly locations: readonly string[];
  readonly startLocations: Readonly<Record<string, readonly string[]>>;
}): Readonly<Record<string, LocationState>> {
  const locations: Record<string, LocationState> = {};
  for (const hex of boardDef.locations) {
    locations[hex] = { controlledBy: null };
  }
  for (const [playerId, hexes] of Object.entries(boardDef.startLocations)) {
    for (const hex of hexes) {
      locations[hex] = { controlledBy: playerId as PlayerId };
    }
  }
  return locations;
}

function buildInitialBoard(boardDef: {
  readonly hexes: readonly string[];
}): Readonly<Record<string, BoardCell | null>> {
  const board: Record<string, BoardCell | null> = {};
  for (const hex of boardDef.hexes) {
    board[hex] = null;
  }
  return board;
}

export function initGame(options: InitGameOptions = {}): WarchestState {
  const config = options.config ?? DEFAULT_CONFIG;
  const boardId = options.boardId ?? DEFAULT_BOARD_ID;
  const boardDef = config.boards[boardId];
  if (!boardDef) throw new Error(`Unknown board: ${boardId}`);

  const p1Units = options.p1UnitTypes ?? DEFAULT_P1_UNITS;
  const p2Units = options.p2UnitTypes ?? DEFAULT_P2_UNITS;

  const seed =
    options.seed ??
    fnv1aHash(`${p1Units.join(",")}:${p2Units.join(",")}:${Date.now()}`);

  let rngState = seed >>> 0;

  const p1Result = buildPlayerZones(p1Units, rngState, config);
  rngState = p1Result.rngState;
  const p2Result = buildPlayerZones(p2Units, rngState, config);
  rngState = p2Result.rngState;

  return {
    phase: "DRAW_COINS",
    turn: 1,
    initiativeHolder: "P1",
    initiativeTransferredThisRound: false,
    activePlayer: "P1",
    playerZones: {
      P1: p1Result.zones,
      P2: p2Result.zones,
    },
    board: buildInitialBoard(boardDef),
    locations: buildInitialLocations(boardDef),
    rngState,
    boardId,
    config,
    winner: null,
    draftState: null,
  };
}

export function createGame(
  options: InitGameOptions & { readonly seed: number },
): WarchestState {
  return initGame(options);
}

// Start a new game beginning with the unit draft phase.
// Both players will alternate picking units before bags are built.
export function initDraftGame(
  options: Omit<InitGameOptions, "p1UnitTypes" | "p2UnitTypes"> = {},
): WarchestState {
  const config = options.config ?? DEFAULT_CONFIG;
  const boardId = options.boardId ?? DEFAULT_BOARD_ID;
  const boardDef = config.boards[boardId];
  if (!boardDef) throw new Error(`Unknown board: ${boardId}`);

  const seed = options.seed ?? fnv1aHash(`draft:${Date.now()}`);
  const rngState = seed >>> 0;

  // Filter ROYAL out of available units (ROYAL is added automatically by buildPlayerZones)
  const availableUnits = Object.keys(config.units).filter(
    (id) => id !== "ROYAL",
  );

  return {
    phase: "DRAFT",
    turn: 1,
    initiativeHolder: "P1",
    initiativeTransferredThisRound: false,
    activePlayer: "P1",
    playerZones: {
      P1: {
        bag: [],
        hand: [],
        discardFaceDown: [],
        discardFaceUp: [],
        supply: [],
        removed: [],
      },
      P2: {
        bag: [],
        hand: [],
        discardFaceDown: [],
        discardFaceUp: [],
        supply: [],
        removed: [],
      },
    },
    board: buildInitialBoard(boardDef),
    locations: buildInitialLocations(boardDef),
    rngState,
    boardId,
    config,
    winner: null,
    draftState: {
      availableUnits,
      picks: { P1: [], P2: [] },
      unitsPerPlayer: 4,
    },
  };
}
